import { useMediaQuery, useTheme } from '@mui/material';
import type { GridColumnVisibilityModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { useOrderByParams } from '~/hooks/order-by';
import UploadDataSourceIllustration from '~/images/illustrations/upload-data-source.svg';
import type { DataSourcesList__DataSource as DataSource } from '../../queries/list.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={UploadDataSourceIllustration}
      header={t('pages.data_sources.list.empty_state.header')}
      description={t('pages.data_sources.list.empty_state.description')}
    />
  );
};

export interface TableProps {
  dataSources: readonly DataSource[];
  loading: boolean;
}

export const Table = ({ dataSources, loading }: TableProps) => {
  const columns = useColumns();
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      kind: !isSmallAndDown,
      keys: !isSmallAndDown,
      foreignIdName: !isSmallAndDown,
      items: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  return (
    <ScrollableDataGrid
      checkboxSelection={false}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowClick={({ row: dataSource }: GridRowParams<DataSource>) =>
        navigate(`${String(dataSource.id)}`)
      }
      rows={dataSources}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
